import { isValidElement } from 'react'
import type { JSX } from 'react'
import { DayPicker } from 'react-day-picker'
import type {
  DateRange,
  ButtonProps,
  CaptionLabelProps,
  ChevronProps,
  MonthCaptionProps,
} from 'react-day-picker'

import {
  Flex,
  IconButton,
  ChevronLeftIcon,
  ChevronRightIcon,
  Label,
} from '@buffer-mono/popcorn'

import styles from './DateRangePicker.module.css'

const MonthCaption = ({
  children,
  className,
}: MonthCaptionProps): JSX.Element => {
  return (
    <Flex className={className} justify="start" align="center" gap="xs">
      {children}
    </Flex>
  )
}

const CaptionLabel = (props: CaptionLabelProps): JSX.Element => {
  // eslint-disable-next-line unused-imports/no-unused-vars
  const { children, ref, color, ...rest } = props
  return <Label {...rest}>{children}</Label>
}

const Chevron = ({
  className,
  size,
  orientation,
  disabled,
}: ChevronProps): JSX.Element => {
  const color = disabled ? 'disabled' : undefined
  return orientation === 'left' ? (
    <ChevronLeftIcon className={className} size={size} color={color} />
  ) : (
    <ChevronRightIcon className={className} size={size} color={color} />
  )
}

const NavigationButton = ({
  children,
  ...buttonProps
}: ButtonProps): JSX.Element => {
  const isPrevious =
    isValidElement<ChevronProps>(children) &&
    children.props.orientation === 'left'
  const label = isPrevious ? 'Go to previous month' : 'Go to next month'
  const icon = isValidElement(children) ? children : <span />
  return (
    <IconButton
      label={label}
      variant="tertiary"
      as="button"
      size="medium"
      {...buttonProps}
    >
      {icon}
    </IconButton>
  )
}

const Weekday = ({
  children,
  ...props
}: JSX.IntrinsicElements['th']): JSX.Element => {
  return (
    <th {...props}>
      <Label size="small" color="subtle">
        {typeof children === 'string' ? children[0] : children}
      </Label>
    </th>
  )
}

interface DateRangePickerProps {
  selected: DateRange | undefined
  onSelect: (range: DateRange | undefined, triggerDate: Date) => void
  month: Date
  onMonthChange: (month: Date) => void
  maxDate?: Date
  weekStartsOn?: 0 | 1
  numberOfMonths?: 1 | 2
  /** When true, stretches the calendar to fill its container width (for use in mobile Drawer) */
  fullWidth?: boolean
}

export function DateRangePicker({
  selected,
  onSelect,
  month,
  onMonthChange,
  maxDate = new Date(),
  weekStartsOn = 0,
  numberOfMonths = 2,
  fullWidth = false,
}: DateRangePickerProps): JSX.Element {
  return (
    <DayPicker
      mode="range"
      numberOfMonths={numberOfMonths}
      month={month}
      onMonthChange={onMonthChange}
      weekStartsOn={weekStartsOn}
      disabled={[{ after: maxDate }]}
      selected={selected}
      onSelect={onSelect}
      className={fullWidth ? `${styles.root} ${styles.fullWidth}` : styles.root}
      classNames={styles}
      components={{
        CaptionLabel,
        Chevron,
        Weekday,
        Button: NavigationButton,
        MonthCaption,
      }}
    />
  )
}
